/* SIZESOFTX
=========================================================================================
* Descripción: Utilidad central de sanitización y bloqueo de inyección (XSS / HTML / URLs).
* Diseñada para NO romper funcionalidad comercial:
*  - Preserva acentos, ñ, diéresis, puntuación comercial, emojis, hashtags, saltos de línea.
*  - Bloquea: tags HTML, <script>, javascript:/data:/vbscript:, manejadores on*=, etc.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1        | 17/09/2026 | Muse Spark                       | Creación blindaje anti-inyección global.
* ---------------------------------------------------------------------------------------
*/

// Patrones inequívocamente maliciosos (no usados en texto comercial legítimo).
const PATRONES_MALICIOSOS = [
  /<\s*script\b/i,
  /<\s*\/\s*script\b/i,
  /<\s*iframe\b/i,
  /<\s*object\b/i,
  /<\s*embed\b/i,
  /<\s*form\b/i,
  /<\s*img\b[^>]*\bon\w+\s*=/i,
  /<\s*svg\b/i,
  /<\s*link\b/i,
  /<\s*meta\b/i,
  /<\s*style\b/i,
  /<\s*a\b[^>]*\bhref\s*=\s*["']?\s*javascript:/i,
  /javascript\s*:/i,
  /data\s*:\s*text\/html/i,
  /vbscript\s*:/i,
  // Manejador de evento dentro de un tag: <... onclick= ...> / <... onerror= ...>
  /<\s*[a-z][^>]*\bon\w+\s*=/i,
  // Manejador suelto típico de inyección: " onclick=" / ' onerror=' al inicio o tras espacio
  /(^|[\s"'`])on(load|error|click|mouseover|focus|submit|change|input|keydown|keyup|mouseenter|mouseleave)\s*=/i,
  /expression\s*\(/i,
  /eval\s*\(/i,
  /document\s*\.\s*(cookie|domain|write)/i,
  /window\s*\.\s*location/i,
];

// URLs permitidas: solo http/https (incluye drive.google, youtube, wa.me, facebook, etc.).
// Se bloquea explícitamente: javascript:, data:, vbscript:, file:, blob: externo (blob interno de preview se maneja aparte).
const PROTOCOLO_PELIGROSO = /^\s*(javascript|data|vbscript|file|ftp)\s*:/i;

export const tieneContenidoMalicioso = (valor) => {
  const texto = String(valor || "");
  if (!texto) return false;
  return PATRONES_MALICIOSOS.some((re) => re.test(texto));
};

export const describirRiesgo = (valor) => {
  const texto = String(valor || "");
  if (/<\s*script\b/i.test(texto)) return "etiqueta <script>";
  if (/<\s*iframe\b/i.test(texto)) return "etiqueta <iframe>";
  if (/javascript\s*:/i.test(texto)) return "protocolo javascript:";
  if (/data\s*:\s*text\/html/i.test(texto)) return "protocolo data:text/html";
  if (/<\s*[a-z][^>]*\bon\w+\s*=/i.test(texto)) return "manejador de evento (on*=)";
  if (/(^|[\s"'`])on(load|error|click|mouseover|focus|submit|change|input|keydown|keyup)\s*=/i.test(texto)) return "manejador de evento (on*=)";
  if (/<\s*(object|embed|form|svg|link|meta|style)\b/i.test(texto)) return "etiqueta HTML no permitida";
  return "contenido no permitido";
};

/**
 * Sanitiza texto de una línea preservando funcionalidad comercial.
 * Quita <>, corchetes de tags, controles y recorta a límite.
 */
export const sanitizarTexto = (valor, limite = 200) => {
  let texto = String(valor || "");
  // Quita controles excepto tab/espacio.
  // eslint-disable-next-line no-control-regex
  texto = texto.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "");
  // Neutraliza delimitadores de tags sin destruir puntuación comercial.
  texto = texto.replace(/[<>]/g, "");
  // Colapsa espacios (preserva una sola línea).
  texto = texto.replace(/\s+/g, " ").trim();
  return texto.slice(0, limite);
};

/** Igual que sanitizarTexto pero preserva saltos de línea (descripciones, beneficios, mensajes). */
export const sanitizarMultilinea = (valor, limite = 2000) => {
  let texto = String(valor || "");
  // eslint-disable-next-line no-control-regex
  texto = texto.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "");
  texto = texto.replace(/[<>]/g, "");
  // Normaliza saltos, limita líneas vacías consecutivas a 2.
  texto = texto.replace(/\r\n?/g, "\n").replace(/\n{4,}/g, "\n\n\n").trim();
  return texto.slice(0, limite);
};

export const sanitizarEmail = (valor, limite = 180) =>
  String(valor || "").trim().toLowerCase().slice(0, limite);

export const sanitizarTelefono = (valor, limite = 25) =>
  sanitizarTexto(valor, limite).replace(/[^0-9+]/g, "").slice(0, limite);

const extraerUrlBruta = (valor, limite) => {
  const texto = sanitizarTexto(valor, limite);
  const markdown = texto.match(/\((https?:\/\/[^)\s]+)\)/i);
  const directa = texto.match(/https?:\/\/[^\s)]+/i);
  return (markdown?.[1] || directa?.[0] || texto).slice(0, limite);
};

/** Extrae y valida URL. Devuelve "" si es insegura. Permite vacío (campos opcionales). */
export const sanitizarUrl = (valor, limite = 700) => {
  const url = extraerUrlBruta(valor, limite).trim();
  if (!url) return "";
  if (!esUrlSegura(url)) return "";
  return url;
};

/** True si la URL es segura para usar en href/src. Permite https/http. Bloquea javascript:, data:, etc. */
export const esUrlSegura = (valor) => {
  const texto = String(valor || "").trim();
  if (!texto) return false;
  // blob: solo se genera localmente para preview; nunca debe llegar al backend como URL guardada.
  if (/^blob:/i.test(texto)) return false;
  if (PROTOCOLO_PELIGROSO.test(texto)) return false;
  // Debe empezar con https?:// para considerarse URL externa válida.
  // Se acepta también www. sin protocolo? No: se exige protocolo para evitar //evil o rutas raras.
  if (!/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(texto)) return false;
  if (tieneContenidoMalicioso(texto)) return false;
  return true;
};

const DRIVE_FILE_RE = /drive\.google\.com\/file\/d\/([^/\s?#]+)/i;
const DRIVE_QS_RE = /[?&]id=([^&\s#]+)/i;

export const extraerDriveIdSeguro = (valor) => {
  const texto = String(valor || "").trim();
  if (tieneContenidoMalicioso(texto)) return null;
  const fileMatch = texto.match(DRIVE_FILE_RE);
  if (fileMatch?.[1] && /^[A-Za-z0-9_-]{10,200}$/.test(fileMatch[1])) return fileMatch[1];
  if (/drive\.google\.com/i.test(texto)) {
    const qs = texto.match(DRIVE_QS_RE);
    if (qs?.[1] && /^[A-Za-z0-9_-]{10,200}$/.test(qs[1])) return qs[1];
  }
  return null;
};

/** Normaliza URL de imagen de Drive a thumbnail seguro; si no es URL segura devuelve "". */
export const normalizarImagenSegura = (valor, limite = 700, respaldo = false) => {
  const url = sanitizarTexto(valor, limite).trim();
  if (!url) return "";
  const driveId = extraerDriveIdSeguro(url);
  if (driveId) {
    return respaldo
      ? `https://drive.google.com/uc?export=view&id=${encodeURIComponent(driveId)}`
      : `https://drive.google.com/thumbnail?id=${encodeURIComponent(driveId)}&sz=w1200`;
  }
  return esUrlSegura(url) ? url : "";
};

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const CODIGO_CAMPANIA_REGEX = /^[A-Z0-9][A-Z0-9_-]{1,59}$/i;

/**
 * Valida un conjunto de campos { etiqueta, valor } contra inyección.
 * Devuelve "" si todo ok, o mensaje "Campo X contiene ...".
 */
export const validarSinInyeccion = (campos = []) => {
  for (const { etiqueta, valor, esUrl } of campos) {
    const texto = String(valor || "");
    if (!texto) continue;
    if (esUrl) {
      if (texto && !esUrlSegura(texto) && !extraerDriveIdSeguro(texto)) {
        return `${etiqueta}: URL no permitida. Usa solo enlaces https:// válidos.`;
      }
      continue;
    }
    if (tieneContenidoMalicioso(texto)) {
      return `${etiqueta} contiene ${describirRiesgo(texto)}, el cual fue bloqueado por seguridad.`;
    }
  }
  return "";
};
